import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Flex, Heading, Text, Button, Badge, Spinner, useToast, useDisclosure,
  SimpleGrid, Input, Select, Textarea, Stack, HStack, VStack, Image, Divider, IconButton,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Link as ChakraLink,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Tabs, TabList, TabPanels, Tab, TabPanel, Tooltip, Wrap, WrapItem, Checkbox,
  ButtonGroup, Alert, AlertIcon, Collapse, useBreakpointValue,
} from "@chakra-ui/react";
import {
  FaSyncAlt, FaCheck, FaBan, FaLink, FaBell, FaPlus, FaTrash,
  FaUserPlus, FaUndo, FaPaperPlane, FaThLarge, FaList, FaHandshake,
} from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import dayjs from "dayjs";
import {
  getSolicitacoes, getSolicitacao, resolverSolicitacao, recusarSolicitacao,
  cancelarSolicitacao, reenviarNotificacaoSolicitacao, moverSolicitacao,
  marcarSolicitacaoEntregue, resolverDiretoSolicitacao,
  getSolicitacaoDestinos, salvarSolicitacaoDestino, removerSolicitacaoDestino,
} from "../services/api";

const LARANJA = "#F68633";

const STATUS_INFO = {
  pendente: { label: "Pendente", color: "yellow" },
  em_andamento: { label: "Em andamento", color: "blue" },
  entrega_bloqueada: { label: "Entrega travada", color: "red" },
  concluida: { label: "Concluída", color: "green" },
  recusada: { label: "Recusada", color: "gray" },
  cancelada: { label: "Cancelada", color: "gray" },
};

const ABERTAS = ["pendente", "em_andamento"];
const FINALIZADAS = ["concluida", "recusada", "cancelada"];

// O card anda da esquerda para a direita. As duas primeiras colunas são a fila
// de trabalho; "Entrega travada" é o que o bot não conseguiu entregar sozinho.
const COLUNAS = [
  {
    key: "pendente", label: "Novas", cor: "yellow", status: ["pendente"],
    ajuda: "O bot pediu e ninguém assumiu ainda.",
  },
  {
    key: "em_andamento", label: "Em andamento", cor: "blue", status: ["em_andamento"],
    ajuda: "Alguém da equipe está cuidando.",
  },
  {
    key: "entrega_bloqueada", label: "Entrega travada", cor: "red", status: ["entrega_bloqueada"],
    ajuda: "Resolvida, mas o WhatsApp recusou o envio. Mande na mão e dê baixa.",
  },
  {
    key: "concluida", label: "Concluídas", cor: "green", status: ["concluida"],
    ajuda: "Resultado entregue ao cliente, pelo bot ou direto pela equipe.",
  },
  {
    key: "sem_solucao", label: "Sem solução", cor: "gray", status: ["recusada", "cancelada"],
    ajuda: "Recusadas (o bot avisou) e canceladas.",
  },
];

const COLUNA_DO_STATUS = COLUNAS.reduce((mapa, col) => {
  col.status.forEach((st) => { mapa[st] = col.key; });
  return mapa;
}, {});

// Para onde cada coluna deixa arrastar. Concluir e recusar pedem dados, então
// o card cai no painel de resolução em vez de mudar de status sozinho.
const DESTINOS = {
  pendente: ["em_andamento", "concluida", "sem_solucao"],
  em_andamento: ["pendente", "concluida", "sem_solucao"],
  entrega_bloqueada: ["concluida"],
  concluida: [],
  sem_solucao: [],
};

const PERIODOS = [
  { v: "24h", label: "24h" },
  { v: "48h", label: "48h" },
  { v: "7d", label: "7 dias" },
  { v: "30d", label: "30 dias" },
  { v: "tudo", label: "Tudo" },
];

const FILTROS_PADRAO = {
  periodo: "7d", tipo: "", fornecedor_id: "", minhas: false, de: "", ate: "", status: "",
};

const lerModo = () => {
  try { return localStorage.getItem("solicitacoes_modo") || "quadro"; } catch { return "quadro"; }
};
const gravarModo = (modo) => {
  try { localStorage.setItem("solicitacoes_modo", modo); } catch { /* modo privativo */ }
};

const minutosDesde = (iso) => (iso ? dayjs().diff(dayjs(iso), "minute") : null);

const duracao = (min) => {
  if (min === null || min === undefined) return "—";
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h${min % 60 ? String(min % 60).padStart(2, "0") : ""}`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ""}`;
};

const haQuanto = (iso) => {
  const m = minutosDesde(iso);
  return m === null ? "" : `há ${duracao(m)}`;
};

// Card em aberto que já esperou demais fica marcado: é o que precisa de gente.
const corEspera = (min) => {
  if (min === null || min === undefined) return "gray.500";
  if (min >= 1440) return "red.500";
  if (min >= 240) return "orange.500";
  return "gray.500";
};

const statusBadge = (s) => {
  const info = STATUS_INFO[s] || { label: s, color: "gray" };
  return <Badge colorScheme={info.color}>{info.label}</Badge>;
};

export default function Solicitacoes() {
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });

  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [resumo, setResumo] = useState({});
  const [tipos, setTipos] = useState({});
  const [fornecedores, setFornecedores] = useState([]);

  const [modo, setModo] = useState(lerModo);
  const [filtros, setFiltros] = useState(FILTROS_PADRAO);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [colunaMobile, setColunaMobile] = useState("pendente");
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [dragOrigem, setDragOrigem] = useState(null);

  const [selecionada, setSelecionada] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [avisoDetalhe, setAvisoDetalhe] = useState("");
  const [recusando, setRecusando] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => { gravarModo(modo); }, [modo]);
  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async (showSpin = true) => {
    if (showSpin) setLoading(true);
    const params = { periodo: filtros.periodo };
    if (filtros.de) params.de = filtros.de;
    if (filtros.ate) params.ate = filtros.ate;
    if (filtros.tipo) params.tipo = filtros.tipo;
    if (filtros.fornecedor_id) params.fornecedor_id = filtros.fornecedor_id;
    if (filtros.minhas) params.minhas = 1;
    if (buscaAplicada) params.busca = buscaAplicada;
    if (modo === "quadro") params.kanban = 1;
    else if (filtros.status) params.status = filtros.status;

    const data = await getSolicitacoes(params);
    if (data) {
      setItens(data.itens?.data || []);
      setTotal(data.itens?.total || 0);
      setStats(data.stats || {});
      setResumo(data.resumo || {});
      setTipos(data.tipos || {});
      setFornecedores(data.fornecedores || []);
    }
    setLoading(false);
  }, [filtros, buscaAplicada, modo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Não atualiza por baixo de quem está trabalhando no card.
  useEffect(() => {
    const t = setInterval(() => {
      if (!isOpen && !recusando && !dragOrigem) carregar(false);
    }, 20000);
    return () => clearInterval(t);
  }, [carregar, isOpen, recusando, dragOrigem]);

  const colunas = useMemo(() => {
    const base = {};
    COLUNAS.forEach((c) => { base[c.key] = []; });
    itens.forEach((s) => {
      const key = COLUNA_DO_STATUS[s.status];
      if (base[key]) base[key].push(s);
    });
    // Fila em ordem de chegada (a mais velha primeiro, é a que está atrasando);
    // o que já foi fechado aparece do mais recente para o mais antigo.
    Object.entries(base).forEach(([key, lista]) => {
      const maisVelhaPrimeiro = ABERTAS.includes(key) || key === "entrega_bloqueada";
      lista.sort((a, b) => (maisVelhaPrimeiro ? a.id - b.id : b.id - a.id));
    });
    return base;
  }, [itens]);

  const abrirDetalhe = async (s, aviso = "") => {
    setSelecionada(s);
    setDetalhe(null);
    setAvisoDetalhe(aviso);
    onOpen();
    const d = await getSolicitacao(s.id);
    if (d) setDetalhe(d);
  };

  const aposAcao = async (msg) => {
    toast({ title: msg, status: "success", duration: 2500, position: "top-right" });
    onClose();
    await carregar(false);
  };

  const mover = async (s, para) => {
    const r = await moverSolicitacao(s.id, para);
    if (!r) {
      toast({ title: "Não foi possível mover", status: "error", duration: 2500, position: "top-right" });
      return;
    }
    toast({
      title: para === "em_andamento" ? `#${s.id} agora é sua` : `#${s.id} voltou para a fila`,
      status: "success", duration: 2000, position: "top-right",
    });
    carregar(false);
  };

  const darBaixaEntrega = async (s) => {
    const r = await marcarSolicitacaoEntregue(s.id);
    if (!r) {
      toast({ title: "Não foi possível dar baixa", status: "error", duration: 2500, position: "top-right" });
      return;
    }
    toast({ title: `#${s.id} marcada como entregue`, status: "success", duration: 2000, position: "top-right" });
    onClose();
    carregar(false);
  };

  const onDragEnd = async ({ source, destination, draggableId }) => {
    setDragOrigem(null);
    if (!destination) return;

    const origem = source.droppableId;
    const destino = destination.droppableId;
    if (origem === destino) return;

    const s = itens.find((i) => String(i.id) === draggableId);
    if (!s) return;

    if (!(DESTINOS[origem] || []).includes(destino)) {
      toast({
        title: "Esse movimento não existe",
        description: FINALIZADAS.includes(s.status)
          ? "Solicitação finalizada não volta para a fila."
          : "Arraste para Em andamento, Concluídas ou Sem solução.",
        status: "info", duration: 2500, position: "top-right",
      });
      return;
    }

    if (destino === "em_andamento" || destino === "pendente") return mover(s, destino);
    if (destino === "sem_solucao") return setRecusando(s);
    if (destino === "concluida") {
      return origem === "entrega_bloqueada"
        ? darBaixaEntrega(s)
        : abrirDetalhe(s, "Anexe o resultado (ou escreva) para o bot entregar ao cliente.");
    }
  };

  const limpar = () => { setFiltros(FILTROS_PADRAO); setBusca(""); setFiltrosAbertos(false); };
  // O período tem chip próprio e a busca tem campo fixo: o contador é só do que
  // fica escondido atrás de "Mais filtros" no celular.
  const filtrosExtras = [filtros.tipo, filtros.fornecedor_id, filtros.status, filtros.de, filtros.ate, filtros.minhas]
    .filter(Boolean).length;
  const filtrando = filtrosExtras > 0 || busca || filtros.periodo !== FILTROS_PADRAO.periodo;

  const acoesDoCard = (s) => {
    const botoes = [];
    if (s.status === "pendente") {
      botoes.push(
        <Button key="assumir" size="xs" leftIcon={<FaUserPlus />} colorScheme="blue" variant="outline"
          onClick={(e) => { e.stopPropagation(); mover(s, "em_andamento"); }}>
          Assumir
        </Button>
      );
    }
    if (s.status === "em_andamento") {
      botoes.push(
        <Button key="resolver" size="xs" leftIcon={<FaCheck />} colorScheme="green" variant="outline"
          onClick={(e) => { e.stopPropagation(); abrirDetalhe(s); }}>
          Resolver
        </Button>,
        <Button key="devolver" size="xs" leftIcon={<FaUndo />} variant="ghost"
          onClick={(e) => { e.stopPropagation(); mover(s, "pendente"); }}>
          Devolver
        </Button>
      );
    }
    if (s.status === "entrega_bloqueada") {
      botoes.push(
        <Button key="entregue" size="xs" leftIcon={<FaPaperPlane />} colorScheme="red" variant="outline"
          onClick={(e) => { e.stopPropagation(); darBaixaEntrega(s); }}>
          Enviei na mão
        </Button>
      );
    }
    return botoes.length ? <HStack spacing={1} mt={2} flexWrap="wrap">{botoes}</HStack> : null;
  };

  const colunasVisiveis = isMobile ? COLUNAS.filter((c) => c.key === colunaMobile) : COLUNAS;

  return (
    <Box bg="gray.50" minH="100vh" pb={10}>
      <Box mx={{ base: 3, md: 10, lg: 20 }} pt={5}>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Heading size="lg">Solicitações</Heading>
        <Button leftIcon={<FaSyncAlt />} size="sm" onClick={() => carregar()} isLoading={loading}>Atualizar</Button>
      </Flex>

      <Tabs colorScheme="orange" variant="enclosed">
        <TabList>
          <Tab>Solicitações</Tab>
          <Tab>Configurações de notificação</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3} mb={4}>
              <CardResumo label="Em aberto" valor={resumo.abertas ?? 0} cor="#B7791F"
                ajuda="Pendentes + em andamento. Não depende do período filtrado." />
              <CardResumo label={`Paradas +${resumo.horas_parada || 4}h`} valor={resumo.paradas ?? 0} cor="#C53030"
                ajuda="Em aberto esperando há mais tempo que isso." />
              <CardResumo label="Entregas travadas" valor={resumo.travadas ?? 0} cor="#9B2C2C"
                ajuda="Resolvidas que o WhatsApp recusou entregar. Precisam de envio manual." />
              <CardResumo label="Concluídas no período" valor={stats.concluida || 0} cor="#2F855A"
                ajuda="O bot entregou o resultado ao cliente." />
              <CardResumo label="Sem solução no período" valor={(stats.recusada || 0) + (stats.cancelada || 0)} cor="#718096"
                ajuda="Recusadas e canceladas." />
              <CardResumo label="Tempo médio" valor={duracao(resumo.tempo_medio_min)} cor="#2C7A7B"
                ajuda="Da abertura até a resolução, no período filtrado." />
            </SimpleGrid>

            <Box bg="white" borderRadius="lg" boxShadow="sm" p={3} mb={4}>
              <Flex justify="space-between" align="center" gap={2} mb={3} wrap="wrap">
                <HStack spacing={1} flexWrap="wrap">
                  {PERIODOS.map((p) => {
                    const ativo = filtros.periodo === p.v && !filtros.de && !filtros.ate;
                    return (
                      <Button
                        key={p.v}
                        size="xs"
                        borderRadius="full"
                        variant={ativo ? "solid" : "outline"}
                        bg={ativo ? LARANJA : "white"}
                        color={ativo ? "white" : "gray.600"}
                        _hover={{ bg: ativo ? "#e07628" : "orange.50" }}
                        onClick={() => setFiltros((f) => ({ ...f, periodo: p.v, de: "", ate: "" }))}
                      >
                        {p.label}
                      </Button>
                    );
                  })}
                </HStack>

                <ButtonGroup size="xs" isAttached variant="outline">
                  <Button leftIcon={<FaThLarge />} onClick={() => setModo("quadro")}
                    bg={modo === "quadro" ? LARANJA : "white"} color={modo === "quadro" ? "white" : "gray.600"}
                    _hover={{ bg: modo === "quadro" ? "#e07628" : "orange.50" }}>
                    Quadro
                  </Button>
                  <Button leftIcon={<FaList />} onClick={() => setModo("lista")}
                    bg={modo === "lista" ? LARANJA : "white"} color={modo === "lista" ? "white" : "gray.600"}
                    _hover={{ bg: modo === "lista" ? "#e07628" : "orange.50" }}>
                    Lista
                  </Button>
                </ButtonGroup>
              </Flex>

              <Flex gap={2} align="center" mb={{ base: 2, md: 0 }}>
                <Input size="sm" flex="1" maxW={{ base: "100%", md: "260px" }} bg="white"
                  placeholder="Cliente, telefone, descrição ou #id"
                  value={busca} onChange={(e) => setBusca(e.target.value)} />
                {isMobile && (
                  <Button size="sm" variant="outline" flexShrink={0} onClick={() => setFiltrosAbertos((v) => !v)}>
                    Filtros{filtrosExtras ? ` (${filtrosExtras})` : ""}
                  </Button>
                )}
              </Flex>

              <Collapse in={!isMobile || filtrosAbertos} animateOpacity>
              <Stack direction={{ base: "column", md: "row" }} spacing={2} align={{ base: "stretch", md: "center" }} flexWrap="wrap">
                <Select size="sm" w={{ base: "100%", md: "190px" }} bg="white" placeholder="Todos os tipos"
                  value={filtros.tipo} onChange={(e) => setFiltros((f) => ({ ...f, tipo: e.target.value }))}>
                  {Object.entries(tipos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </Select>
                <Select size="sm" w={{ base: "100%", md: "190px" }} bg="white" placeholder="Todos os fornecedores"
                  value={filtros.fornecedor_id} onChange={(e) => setFiltros((f) => ({ ...f, fornecedor_id: e.target.value }))}>
                  {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                </Select>
                {modo === "lista" && (
                  <Select size="sm" w={{ base: "100%", md: "180px" }} bg="white" placeholder="Todos os status"
                    value={filtros.status} onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
                    {Object.entries(STATUS_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </Select>
                )}
                <HStack spacing={1} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1}>
                  <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">Aberta de</Text>
                  <Input size="xs" type="date" maxW="140px" variant="unstyled" value={filtros.de}
                    onChange={(e) => setFiltros((f) => ({ ...f, de: e.target.value }))} />
                  <Text fontSize="xs" color="gray.500">até</Text>
                  <Input size="xs" type="date" maxW="140px" variant="unstyled" value={filtros.ate}
                    onChange={(e) => setFiltros((f) => ({ ...f, ate: e.target.value }))} />
                  {(filtros.de || filtros.ate) && (
                    <IconButton aria-label="Limpar datas" size="xs" variant="ghost" icon={<FaTrash />}
                      onClick={() => setFiltros((f) => ({ ...f, de: "", ate: "" }))} />
                  )}
                </HStack>
                <Checkbox size="sm" colorScheme="orange" isChecked={filtros.minhas}
                  onChange={(e) => setFiltros((f) => ({ ...f, minhas: e.target.checked }))}>
                  <Text fontSize="sm" whiteSpace="nowrap">Só as minhas</Text>
                </Checkbox>
                {filtrando && <Button size="xs" variant="ghost" onClick={limpar}>Limpar filtros</Button>}
              </Stack>
              </Collapse>
            </Box>

            {loading ? (
              <Flex justify="center" py={10}><Spinner size="xl" color={LARANJA} /></Flex>
            ) : modo === "quadro" ? (
              <>
                {isMobile && (
                  <Wrap spacing={2} mb={3}>
                    {COLUNAS.map((c) => (
                      <WrapItem key={c.key}>
                        <Button size="xs" borderRadius="full"
                          variant={colunaMobile === c.key ? "solid" : "outline"}
                          colorScheme={colunaMobile === c.key ? c.cor : "gray"}
                          onClick={() => setColunaMobile(c.key)}>
                          {c.label} ({colunas[c.key].length})
                        </Button>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}

                <DragDropContext onDragStart={({ source }) => setDragOrigem(source.droppableId)} onDragEnd={onDragEnd}>
                  <SimpleGrid columns={{ base: 1, lg: 3, xl: 5 }} spacing={3} alignItems="start">
                    {colunasVisiveis.map((col) => {
                      const podeSoltar = !dragOrigem || (DESTINOS[dragOrigem] || []).includes(col.key) || dragOrigem === col.key;
                      return (
                        <Droppable droppableId={col.key} key={col.key} isDropDisabled={!podeSoltar}>
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              bg={snapshot.isDraggingOver ? "orange.50" : "gray.100"}
                              borderWidth="1px"
                              borderColor={snapshot.isDraggingOver ? "orange.200" : "gray.200"}
                              borderRadius="xl"
                              p={2}
                              minH={{ base: "auto", lg: "200px" }}
                              opacity={podeSoltar ? 1 : 0.45}
                              transition="background 0.12s, opacity 0.12s"
                            >
                              <HStack mb={1} px={1} justify="space-between">
                                <HStack spacing={2}>
                                  <Box w="8px" h="8px" borderRadius="full" bg={`${col.cor}.400`} />
                                  <Text fontWeight="bold" fontSize="sm">{col.label}</Text>
                                </HStack>
                                <Badge colorScheme={col.cor} borderRadius="full">{colunas[col.key].length}</Badge>
                              </HStack>
                              <Text fontSize="11px" color="gray.500" px={1} mb={2}>{col.ajuda}</Text>

                              <VStack align="stretch" spacing={2}>
                                {colunas[col.key].map((s, idx) => (
                                  <Draggable draggableId={String(s.id)} index={idx} key={s.id} isDragDisabled={!!isMobile}>
                                    {(prov, snap) => (
                                      <Box
                                        ref={prov.innerRef}
                                        {...prov.draggableProps}
                                        {...prov.dragHandleProps}
                                        bg="white"
                                        borderWidth="1px"
                                        borderColor="gray.100"
                                        borderLeftWidth="4px"
                                        borderLeftColor={`${col.cor}.400`}
                                        borderRadius="lg"
                                        p={3}
                                        boxShadow={snap.isDragging ? "lg" : "xs"}
                                        cursor="pointer"
                                        _hover={{ boxShadow: "md", borderColor: "orange.200" }}
                                        onClick={() => abrirDetalhe(s)}
                                      >
                                        <CardSolicitacao s={s} acoes={acoesDoCard(s)} />
                                      </Box>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                                {colunas[col.key].length === 0 && (
                                  <Text fontSize="xs" color="gray.400" textAlign="center" py={6}>
                                    {col.key === "pendente" ? "nada na fila 🎉" : "—"}
                                  </Text>
                                )}
                              </VStack>
                            </Box>
                          )}
                        </Droppable>
                      );
                    })}
                  </SimpleGrid>
                </DragDropContext>

                {total > itens.length && (
                  <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
                    Mostrando {itens.length} de {total} no período — use um período menor ou a busca para ver o resto.
                  </Text>
                )}
              </>
            ) : itens.length === 0 ? (
              <Text color="gray.500" py={8} textAlign="center">Nenhuma solicitação encontrada.</Text>
            ) : (
              <>
                {/* Desktop: tabela */}
                <TableContainer bg="white" borderRadius="lg" boxShadow="sm" display={{ base: "none", lg: "block" }}>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>#</Th><Th>Tipo</Th><Th>Cliente</Th><Th>Fornecedor</Th>
                        <Th>Descrição</Th><Th>Status</Th><Th>Responsável</Th><Th>Aberta</Th><Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {itens.map((s) => (
                        <Tr key={s.id} _hover={{ bg: "gray.50" }}>
                          <Td>{s.id}</Td>
                          <Td><Badge colorScheme="orange">{s.tipo_label}</Badge></Td>
                          <Td>
                            <Text fontSize="sm" fontWeight="600">{s.nome_cliente || "—"}</Text>
                            <Text fontSize="xs" color="gray.500">{s.telefone_cliente}</Text>
                          </Td>
                          <Td fontSize="sm">{s.fornecedor_nome || "—"}</Td>
                          <Td maxW="280px"><Text fontSize="sm" noOfLines={2}>{s.descricao}</Text></Td>
                          <Td>{statusBadge(s.status)}</Td>
                          <Td fontSize="xs" color="gray.600">{s.atendente || s.atendido_por_externo || "—"}</Td>
                          <Td fontSize="xs" color={ABERTAS.includes(s.status) ? corEspera(minutosDesde(s.created_at)) : "gray.500"}>
                            {s.created_at ? dayjs(s.created_at).format("DD/MM HH:mm") : ""}
                            <Text fontSize="10px" color="gray.400">{haQuanto(s.created_at)}</Text>
                          </Td>
                          <Td><Button size="xs" onClick={() => abrirDetalhe(s)}>Abrir</Button></Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>

                {/* Mobile/tablet: cards */}
                <VStack align="stretch" spacing={2} display={{ base: "flex", lg: "none" }}>
                  {itens.map((s) => (
                    <Box key={s.id} bg="white" borderRadius="lg" boxShadow="sm" p={3}
                      borderLeftWidth="4px" borderLeftColor={`${STATUS_INFO[s.status]?.color || "gray"}.400`}
                      onClick={() => abrirDetalhe(s)} cursor="pointer">
                      <Flex justify="space-between" align="center" mb={1} gap={2}>
                        <Badge colorScheme="orange">{s.tipo_label}</Badge>
                        {statusBadge(s.status)}
                      </Flex>
                      <CardSolicitacao s={s} acoes={acoesDoCard(s)} semTipo />
                    </Box>
                  ))}
                </VStack>

                {total > itens.length && (
                  <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
                    Mostrando {itens.length} de {total}. Refine o período ou a busca.
                  </Text>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel px={0}>
            <ConfigDestinos tipos={tipos} />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Drawer isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "md" }}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Solicitação #{selecionada?.id}</DrawerHeader>
          <DrawerBody>
            {!detalhe ? (
              <Flex justify="center" py={10}><Spinner color={LARANJA} /></Flex>
            ) : (
              <DetalheSolicitacao
                s={detalhe}
                aviso={avisoDetalhe}
                onAcao={aposAcao}
                onMover={mover}
                onEntregue={darBaixaEntrega}
                onRecusar={() => { setRecusando(detalhe); onClose(); }}
                toast={toast}
              />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <ModalRecusa
        s={recusando}
        onClose={() => setRecusando(null)}
        onFeito={(msg) => { setRecusando(null); aposAcao(msg); }}
        toast={toast}
      />
      </Box>
    </Box>
  );
}

function CardResumo({ label, valor, cor, ajuda }) {
  return (
    <Tooltip label={ajuda} openDelay={400} hasArrow>
      <Box bg="white" p={3} borderRadius="lg" boxShadow="sm" borderLeftWidth="4px" borderLeftColor={cor}>
        <Text fontSize="2xl" fontWeight="bold" color={cor} lineHeight="1.2">{valor}</Text>
        <Text fontSize="xs" color="gray.600" noOfLines={2}>{label}</Text>
      </Box>
    </Tooltip>
  );
}

function CardSolicitacao({ s, acoes, semTipo }) {
  const min = minutosDesde(s.created_at);
  const aberta = ABERTAS.includes(s.status);

  return (
    <>
      <Flex justify="space-between" align="start" gap={2} mb={1}>
        {!semTipo && <Badge colorScheme="orange" fontSize="10px">{s.tipo_label}</Badge>}
        <Text fontSize="10px" fontWeight={aberta && min >= 240 ? "700" : "500"} whiteSpace="nowrap"
          color={aberta ? corEspera(min) : "gray.400"} ml="auto">
          {haQuanto(s.created_at)}
        </Text>
      </Flex>

      <Text fontSize="sm" fontWeight="600" noOfLines={1}>{s.nome_cliente || "Cliente sem nome"}</Text>
      {s.telefone_cliente && <Text fontSize="xs" color="gray.500">{s.telefone_cliente}</Text>}
      <Text fontSize="xs" color="gray.700" noOfLines={2} mt={1}>{s.descricao}</Text>

      {(s.produto_codigo || s.produto_nome) && (
        <Text fontSize="11px" color="gray.500" noOfLines={1} mt={1}>
          {s.produto_codigo ? `${s.produto_codigo} · ` : ""}{s.produto_nome}
        </Text>
      )}

      <Text fontSize="10px" color="gray.400" noOfLines={1} mt={2}>
        #{s.id}
        {s.fornecedor_nome ? ` · ${s.fornecedor_nome}` : ""}
        {s.atendente ? ` · ${s.atendente}` : ""}
        {!s.atendente && s.atendido_por_externo ? ` · ${s.atendido_por_externo}` : ""}
      </Text>

      {acoes}
    </>
  );
}

function DetalheSolicitacao({ s, aviso, onAcao, onMover, onEntregue, onRecusar, toast }) {
  const [arquivos, setArquivos] = useState([]);
  const [resultadoTexto, setResultadoTexto] = useState("");
  const [resolucao, setResolucao] = useState("");
  const [busy, setBusy] = useState(false);

  const finalizada = FINALIZADAS.includes(s.status);
  const travada = s.status === "entrega_bloqueada";

  const resolver = async () => {
    if (!arquivos.length && !resultadoTexto.trim()) {
      toast({ title: "Anexe um arquivo ou informe o resultado", status: "warning", duration: 2500, position: "top-right" });
      return;
    }
    setBusy(true);
    const form = new FormData();
    arquivos.forEach((f) => form.append("arquivos[]", f));
    if (resultadoTexto) form.append("resultado_texto", resultadoTexto);
    if (resolucao) form.append("resolucao", resolucao);
    const ok = await resolverSolicitacao(s.id, form);
    setBusy(false);
    if (ok) onAcao("Resolvida — o bot vai entregar ao cliente");
    else toast({ title: "Falha ao resolver", status: "error", duration: 3000, position: "top-right" });
  };

  const cancelar = async () => {
    setBusy(true);
    const ok = await cancelarSolicitacao(s.id);
    setBusy(false);
    if (ok) onAcao("Cancelada");
  };

  // Atendido por fora (ligação, WhatsApp próprio): fecha sem o bot mandar nada.
  // A observação interna do formulário vai junto como registro.
  const resolverDireto = async () => {
    setBusy(true);
    const ok = await resolverDiretoSolicitacao(s.id, resolucao.trim() ? { resolucao } : {});
    setBusy(false);
    if (ok) onAcao("Concluída — o bot não vai mandar nada ao cliente");
    else toast({ title: "Falha ao concluir", status: "error", duration: 3000, position: "top-right" });
  };

  const reenviar = async () => {
    const r = await reenviarNotificacaoSolicitacao(s.id);
    toast({
      title: r ? `Notificação reenviada (${r.enviados || 0})` : "Falha ao reenviar",
      status: r ? "success" : "error", duration: 2500, position: "top-right",
    });
  };

  const copiarLink = () => {
    navigator.clipboard?.writeText(s.link_publico);
    toast({ title: "Link copiado", status: "success", duration: 1500, position: "top-right" });
  };

  const linha = (label, valor) => valor ? (
    <Box><Text fontSize="xs" color="gray.500">{label}</Text><Text fontSize="sm">{valor}</Text></Box>
  ) : null;

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between">
        <Badge colorScheme="orange">{s.tipo_label}</Badge>
        {statusBadge(s.status)}
      </HStack>

      {aviso && (
        <Alert status="info" borderRadius="md" fontSize="sm" py={2}>
          <AlertIcon />{aviso}
        </Alert>
      )}

      {linha("Aberta em", s.created_at ? `${dayjs(s.created_at).format("DD/MM/YYYY HH:mm")} (${haQuanto(s.created_at)})` : null)}
      {linha("Cliente", s.nome_cliente)}
      {linha("Telefone", s.telefone_cliente)}
      {linha("Fornecedor", s.fornecedor_nome)}
      {linha("Produto", s.produto_nome)}
      {linha("Código", s.produto_codigo)}
      <Box><Text fontSize="xs" color="gray.500">O que o cliente pediu</Text><Text fontSize="sm">{s.descricao}</Text></Box>
      {linha("Resultado (texto)", s.resultado_texto)}
      {linha("Observação", s.resolucao)}
      {linha("Responsável", s.atendente)}
      {linha("Resolvida por", s.atendido_por_externo)}
      {linha("Resposta enviada", s.bot_resposta)}

      {Array.isArray(s.anexos) && s.anexos.length > 0 && (
        <Box>
          <Text fontSize="xs" color="gray.500" mb={1}>Anexos</Text>
          <Wrap>
            {s.anexos.map((a, i) => (
              <WrapItem key={i}>
                <ChakraLink href={a.url} isExternal>
                  {a.tipo === "image"
                    ? <Image src={a.url} alt={a.nome} boxSize="72px" objectFit="cover" borderRadius="md" />
                    : <Box boxSize="72px" bg="gray.100" borderRadius="md" display="flex" alignItems="center" justifyContent="center" fontSize="xs" p={1} textAlign="center">{a.nome || a.tipo}</Box>}
                </ChakraLink>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      <HStack flexWrap="wrap" spacing={2}>
        <Tooltip label={s.link_publico}><Button size="xs" leftIcon={<FaLink />} onClick={copiarLink}>Copiar link público</Button></Tooltip>
        {!finalizada && <Button size="xs" leftIcon={<FaBell />} variant="outline" onClick={reenviar}>Reenviar aviso</Button>}
        {s.status === "pendente" && (
          <Button size="xs" leftIcon={<FaUserPlus />} colorScheme="blue" variant="outline"
            onClick={() => onMover(s, "em_andamento")}>Assumir</Button>
        )}
        {s.status === "em_andamento" && (
          <Button size="xs" leftIcon={<FaUndo />} variant="ghost" onClick={() => onMover(s, "pendente")}>Devolver para a fila</Button>
        )}
      </HStack>

      <Divider />

      {travada ? (
        <>
          <Alert status="warning" borderRadius="md" fontSize="sm" py={2}>
            <AlertIcon />
            O bot não conseguiu entregar (provável janela de 24h do WhatsApp). Envie o resultado por fora e dê baixa aqui.
          </Alert>
          <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
            <Button size="sm" colorScheme="red" leftIcon={<FaPaperPlane />} onClick={() => onEntregue(s)}>
              Já enviei ao cliente
            </Button>
            <Button size="sm" variant="ghost" onClick={cancelar} isLoading={busy}>Cancelar solicitação</Button>
          </Stack>
        </>
      ) : finalizada ? (
        <Text fontSize="sm" color="gray.500">
          {s.entregue_em ? `Entregue ao cliente em ${dayjs(s.entregue_em).format("DD/MM/YYYY HH:mm")}.` : "Solicitação finalizada."}
        </Text>
      ) : (
        <>
          <Box>
            <Text fontWeight="600" mb={2}>Resolver</Text>
            <Input type="file" multiple accept="image/*,video/*,application/pdf" p={1} mb={2}
              onChange={(e) => setArquivos(Array.from(e.target.files || []))} />
            <Input placeholder="Resultado em texto (ex.: código de rastreio)" mb={2}
              value={resultadoTexto} onChange={(e) => setResultadoTexto(e.target.value)} />
            <Textarea placeholder="Observação interna (opcional)" rows={2} mb={2}
              value={resolucao} onChange={(e) => setResolucao(e.target.value)} />
            <Button bg={LARANJA} color="white" _hover={{ bg: "#e07628" }} leftIcon={<FaCheck />}
              isLoading={busy} onClick={resolver} w="100%">
              Concluir e entregar ao cliente
            </Button>
          </Box>
          {/* Cancelar fica dentro de "Não será possível", ao lado de "Recusar e
              avisar": cancelar direto daqui deixava o cliente esperando o retorno
              prometido sem receber nada. */}
          <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
            <Button size="sm" colorScheme="green" variant="outline" leftIcon={<FaHandshake />}
              onClick={resolverDireto} isLoading={busy}>
              Já resolvi com o cliente
            </Button>
            <Button size="sm" colorScheme="red" variant="outline" leftIcon={<FaBan />} onClick={onRecusar} isDisabled={busy}>
              Não será possível
            </Button>
          </Stack>
        </>
      )}
    </VStack>
  );
}

/**
 * Sai da fila sem resultado: recusar faz o bot avisar o cliente; cancelar é
 * silencioso (some do quadro sem mandar nada).
 */
function ModalRecusa({ s, onClose, onFeito, toast }) {
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setMotivo(""); }, [s]);

  const recusar = async () => {
    if (!motivo.trim()) return;
    setBusy(true);
    const ok = await recusarSolicitacao(s.id, { resolucao: motivo });
    setBusy(false);
    if (ok) onFeito("Recusada — o bot vai avisar o cliente");
    else toast({ title: "Falha ao recusar", status: "error", duration: 3000, position: "top-right" });
  };

  const cancelar = async () => {
    setBusy(true);
    const ok = await cancelarSolicitacao(s.id);
    setBusy(false);
    if (ok) onFeito("Cancelada (o cliente não foi avisado)");
    else toast({ title: "Falha ao cancelar", status: "error", duration: 3000, position: "top-right" });
  };

  return (
    <Modal isOpen={!!s} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent mx={3}>
        <ModalHeader>Não será possível atender {s ? `#${s.id}` : ""}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Textarea placeholder="Motivo (o bot adapta para o cliente)" rows={3}
            value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          <Text fontSize="xs" color="gray.500" mt={2}>
            Só cancelar: o cliente ouviu que teria retorno e não vai receber nada.
            Se já falou com ele por fora, use "Já resolvi com o cliente".
          </Text>
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          <Button variant="ghost" size="sm" onClick={cancelar} isLoading={busy}>Só cancelar</Button>
          <Button colorScheme="red" size="sm" onClick={recusar} isLoading={busy} isDisabled={!motivo.trim()}>
            Recusar e avisar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ConfigDestinos({ tipos }) {
  const toast = useToast();
  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novo, setNovo] = useState({ fornecedor_id: "", tipo: "", nome: "", whatsapp: "", ativo: true });

  const carregar = useCallback(async () => {
    setLoading(true);
    const data = await getSolicitacaoDestinos();
    if (data) setDestinos(data.destinos || []);
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!novo.whatsapp.trim()) {
      toast({ title: "Informe o WhatsApp", status: "warning", duration: 2000, position: "top-right" });
      return;
    }
    const payload = {
      fornecedor_id: novo.fornecedor_id ? Number(novo.fornecedor_id) : null,
      tipo: novo.tipo || null,
      nome: novo.nome || null,
      whatsapp: novo.whatsapp,
      ativo: true,
    };
    const ok = await salvarSolicitacaoDestino(payload);
    if (ok) {
      setNovo({ fornecedor_id: "", tipo: "", nome: "", whatsapp: "", ativo: true });
      carregar();
    } else {
      toast({ title: "Falha ao salvar", status: "error", duration: 2500, position: "top-right" });
    }
  };

  const remover = async (id) => {
    const ok = await removerSolicitacaoDestino(id);
    if (ok) carregar();
  };

  return (
    <Box>
      <Text color="gray.600" mb={3} fontSize="sm">
        Quem recebe o aviso de nova solicitação no WhatsApp. Sem fornecedor/tipo = vale para todos (global).
        O mais específico (fornecedor + tipo) tem prioridade.
      </Text>

      <Box bg="white" p={3} borderRadius="lg" boxShadow="sm" mb={4}>
        <SimpleGrid columns={[1, 2, 5]} spacing={2} alignItems="end">
          <Box>
            <Text fontSize="xs" color="gray.500">Fornecedor ID</Text>
            <Input size="sm" placeholder="(global)" value={novo.fornecedor_id}
              onChange={(e) => setNovo((n) => ({ ...n, fornecedor_id: e.target.value }))} />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Tipo</Text>
            <Select size="sm" placeholder="(todos)" value={novo.tipo}
              onChange={(e) => setNovo((n) => ({ ...n, tipo: e.target.value }))}>
              {Object.entries(tipos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">Rótulo</Text>
            <Input size="sm" placeholder="ex.: Foto Morelate" value={novo.nome}
              onChange={(e) => setNovo((n) => ({ ...n, nome: e.target.value }))} />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500">WhatsApp (com DDI)</Text>
            <Input size="sm" placeholder="5511999999999" value={novo.whatsapp}
              onChange={(e) => setNovo((n) => ({ ...n, whatsapp: e.target.value }))} />
          </Box>
          <Button size="sm" w={{ base: "100%", md: "auto" }} bg={LARANJA} color="white" _hover={{ bg: "#e07628" }} leftIcon={<FaPlus />} onClick={salvar}>Adicionar</Button>
        </SimpleGrid>
      </Box>

      {loading ? (
        <Flex justify="center" py={6}><Spinner color={LARANJA} /></Flex>
      ) : destinos.length === 0 ? (
        <Text color="gray.500" py={3} textAlign="center">Nenhum número configurado.</Text>
      ) : (
        <>
          {/* Desktop: tabela */}
          <TableContainer bg="white" borderRadius="lg" boxShadow="sm" display={{ base: "none", md: "block" }}>
            <Table size="sm">
              <Thead>
                <Tr><Th>Rótulo</Th><Th>Fornecedor</Th><Th>Tipo</Th><Th>WhatsApp</Th><Th></Th></Tr>
              </Thead>
              <Tbody>
                {destinos.map((d) => (
                  <Tr key={d.id}>
                    <Td>{d.nome || "—"}</Td>
                    <Td>{d.fornecedor_id ? `#${d.fornecedor_id}` : <Badge>global</Badge>}</Td>
                    <Td>{d.tipo ? (tipos[d.tipo] || d.tipo) : <Badge>todos</Badge>}</Td>
                    <Td>{d.whatsapp}</Td>
                    <Td><IconButton aria-label="Remover" size="xs" colorScheme="red" variant="ghost" icon={<FaTrash />} onClick={() => remover(d.id)} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Mobile: cards */}
          <VStack align="stretch" spacing={2} display={{ base: "flex", md: "none" }}>
            {destinos.map((d) => (
              <Flex key={d.id} bg="white" borderRadius="lg" boxShadow="sm" p={3} justify="space-between" align="center" gap={2}>
                <Box minW={0}>
                  <Text fontSize="sm" fontWeight="600" noOfLines={1}>{d.nome || d.whatsapp}</Text>
                  <Text fontSize="xs" color="gray.600">{d.whatsapp}</Text>
                  <HStack spacing={1} mt={1}>
                    {d.fornecedor_id ? <Badge>#{d.fornecedor_id}</Badge> : <Badge>global</Badge>}
                    {d.tipo ? <Badge colorScheme="orange">{tipos[d.tipo] || d.tipo}</Badge> : <Badge>todos</Badge>}
                  </HStack>
                </Box>
                <IconButton aria-label="Remover" size="sm" colorScheme="red" variant="ghost" icon={<FaTrash />} onClick={() => remover(d.id)} />
              </Flex>
            ))}
          </VStack>
        </>
      )}
    </Box>
  );
}
